import { ApolloLink, Observable } from '@apollo/client';
import { isQueryOperation } from '@apollo/client/utilities';
import { hashStringToHex, stableStringify } from '@provision/formatting-utils';
// The default TTL is set to 12 hours. This extended duration is acceptable because we have a global query
// invalidation handler that listens for WebSocket events. If a relevant record is created or updated, the
// query will be invalidated automatically, eliminating the need to wait for the TTL to expire.
const DEFAULT_TTL_MS = 43200000;
const INVALIDATE_KEY = 'invalidate';
export const INVALIDATE_QUERY = { [INVALIDATE_KEY]: true };
// Queries of the same name can use different query parameters and should be cached separately.
const generateCacheKey = (operationName, variables) => {
    return `${operationName}:${hashStringToHex(stableStringify(variables))}`;
};
export const createTtlLink = (config = {}) => {
    const { defaultTtl = DEFAULT_TTL_MS } = config;
    // This data structure will evaluate whether request should be forwarded to the server
    // or query response is fresh and can be retrieved from ApolloCache.
    // Initialize from localStorage
    const queryTimeStampsMap = new Map();
    const runGarbageCollection = () => {
        const now = Date.now();
        // The grace period after TTL expiration (1 hour = 3,600,000 ms)
        const gracePeriod = 3600000;
        for (const [key, meta] of queryTimeStampsMap.entries()) {
            if (now - meta.timestamp >= meta.ttl + gracePeriod) {
                queryTimeStampsMap.delete(key);
            }
        }
    };
    setInterval(runGarbageCollection, DEFAULT_TTL_MS);
    const ttlLink = new ApolloLink((operation, forward) => {
        const { operationName, variables, query } = operation;
        /*
         * Only cache GraphQL query operations.
         * Non-query operations (like mutations) should not be cached.
         */
        if (!isQueryOperation(query)) {
            return forward(operation);
        }
        const context = operation.getContext();
        // ttl is set to 0 - exit early and forward the operation so apollo can proceed with handling it
        if (context.ttl === 0) {
            return forward(operation);
        }
        // Check if the cached response is still valid based on TTL
        const key = generateCacheKey(operationName, variables);
        const cachedQueryMeta = queryTimeStampsMap.get(key);
        const shouldInvalidate = variables?.[INVALIDATE_KEY] === true;
        const { __partnerId, ...rawVariables } = variables ?? {};
        // Always read cached data (if any)
        const cachedData = context.cache.readQuery({ query, variables: rawVariables });
        // Check if the data is still "fresh"
        const isFresh = cachedQueryMeta && Date.now() - cachedQueryMeta.timestamp < cachedQueryMeta.ttl;
        if (cachedData && isFresh && !shouldInvalidate) {
            // Fresh data exists – return cached data and skip the network call
            return new Observable((observer) => {
                // deferring observer.next to avoid blocking main thread
                setTimeout(() => {
                    observer.next({ data: cachedData });
                    observer.complete();
                }, 0);
            });
        }
        if (cachedData) {
            // Data exists but is stale – return the stale data immediately
            // and trigger a network request to update it.
            return new Observable((observer) => {
                // Emit cached data as a fallback.
                setTimeout(() => {
                    observer.next({ data: cachedData });
                }, 0);
                // Now perform the network request.
                forward(operation).subscribe({
                    next: (response) => {
                        // Update cache metadata upon receiving new data.
                        queryTimeStampsMap.set(key, {
                            timestamp: Date.now(),
                            ttl: context.ttl ?? defaultTtl,
                        });
                        // Emit the fresh response.
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error) => {
                        observer.error(error);
                    },
                });
            });
        }
        // No cached data – perform the network request
        return forward(operation).map((response) => {
            queryTimeStampsMap.set(key, {
                timestamp: Date.now(),
                ttl: context.ttl ?? defaultTtl,
            });
            return response;
        });
    });
    const invalidateQuery = (operationName, variables) => {
        const key = generateCacheKey(operationName, variables);
        const data = queryTimeStampsMap.get(key);
        if (data) {
            // instead of deleting we mark the query as stale by setting its timestamp to an expired value.
            // so it can still return data, but will trigger api call anyway
            queryTimeStampsMap.set(key, {
                timestamp: Date.now() - (defaultTtl + 1), // >TTL old
                ttl: defaultTtl,
            });
        }
    };
    const invalidateAll = () => {
        queryTimeStampsMap.clear();
    };
    return { link: ttlLink, invalidateQuery, invalidateAll };
};
